import { KeyRound, Pencil, RefreshCw, Trash2 } from "lucide-react";
import { useState, type FormEvent, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  useProject,
  useRefreshCatalogue,
  useRenameProject,
  useReplaceToken,
} from "../api/queries.js";
import { ErrorMessage, fieldError } from "../ui/feedback.js";
import { dateTime } from "../ui/format.js";
import { FieldHint } from "../ui/help.js";
import { PageHeader, Section } from "../ui/layout.js";
import { Dialog } from "../ui/overlay.js";
import { Alert, Button, Card, Spinner, TextField } from "../ui/primitives.js";
import { useToast } from "../ui/toast.js";
import { ConnectionBadges, ForgetProjectDialog } from "./projects.js";

function Row({ label, info, children }: { label: string; info?: ReactNode; children: ReactNode }) {
  return (
    <div className="grid gap-1 py-3 sm:grid-cols-[14rem_1fr] sm:gap-4">
      <dt className="flex items-center gap-1.5 text-sm text-muted">
        {label}
        {info}
      </dt>
      <dd className="text-sm">{children}</dd>
    </div>
  );
}

function ReplaceTokenDialog({
  projectId,
  open,
  onOpenChange,
}: {
  projectId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const replace = useReplaceToken(projectId);
  const toast = useToast();
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const credential = String(new FormData(event.currentTarget).get("credential") ?? "").trim();
    replace.mutate(credential, {
      onSuccess: () => {
        toast({ tone: "ok", title: t("connection.tokenReplaced") });
        onOpenChange(false);
      },
    });
  };
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={t("projects.replaceToken")}
      description={t("connection.replaceLead")}
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" form="replace-token" busy={replace.isPending}>
            {t("connection.replace")}
          </Button>
        </>
      }
    >
      <form id="replace-token" onSubmit={submit} className="flex flex-col gap-4">
        <TextField
          label={t("projects.token")}
          name="credential"
          type="password"
          required
          autoComplete="off"
          spellCheck={false}
          hint={t("projects.tokenHelp")}
          error={fieldError(replace.error, "credential", t)}
        />
        <ErrorMessage error={replace.error} />
      </form>
    </Dialog>
  );
}

function RenameDialog({
  projectId,
  name,
  open,
  onOpenChange,
}: {
  projectId: string;
  name: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const rename = useRenameProject(projectId);
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const next = String(new FormData(event.currentTarget).get("name") ?? "").trim();
    rename.mutate(next, { onSuccess: () => onOpenChange(false) });
  };
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={t("connection.rename")}
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" form="rename-project" busy={rename.isPending}>
            {t("common.save")}
          </Button>
        </>
      }
    >
      <form id="rename-project" onSubmit={submit} className="flex flex-col gap-4">
        <TextField
          label={t("projects.name")}
          name="name"
          defaultValue={name}
          required
          maxLength={64}
          hint={t("projects.nameHint")}
          error={fieldError(rename.error, "name", t)}
        />
        <ErrorMessage error={rename.error} />
      </form>
    </Dialog>
  );
}

/** The provider connection of one project: state, token, catalogue, removal. */
export function ConnectionPage({ projectId }: { projectId: string }) {
  const { t, i18n } = useTranslation();
  const project = useProject(projectId);
  const refresh = useRefreshCatalogue();
  const toast = useToast();
  const [dialog, setDialog] = useState<"token" | "rename" | "forget" | null>(null);
  if (project.isPending) return <Spinner label={t("common.loading")} />;
  if (project.error) return <ErrorMessage error={project.error} />;
  const c = project.data.connection;
  // The token, the name and removing the project are for administrators (docs/users.md).
  const admin = project.data.access === "admin";
  const closeDialog = (open: boolean) => !open && setDialog(null);

  return (
    <>
      <PageHeader
        title={t("nav.connection")}
        intro={t("connection.intro")}
        help="connection"
        meta={<ConnectionBadges project={project.data} />}
      />
      {!admin && <Alert tone="neutral">{t("connection.adminOnly")}</Alert>}
      {c?.lastError && (
        <Alert tone={c.status === "revoked" ? "danger" : "warn"}>
          <p className="font-medium">{t(`connection.problem.${c.status}`)}</p>
          <p className="mt-1 text-xs opacity-80">{c.lastError}</p>
        </Alert>
      )}
      <Card>
        <dl className="divide-y divide-line">
          <Row label={t("projects.name")}>
            <span className="flex items-center gap-2">
              {project.data.name}
              {admin && (
                <button
                  type="button"
                  onClick={() => setDialog("rename")}
                  className="rounded p-1.5 text-muted hover:bg-surface-2 hover:text-text"
                  aria-label={t("connection.rename")}
                >
                  <Pencil className="size-3.5" aria-hidden />
                </button>
              )}
            </span>
          </Row>
          <Row label={t("connection.provider")}>Hetzner Cloud</Row>
          {c && (
            <>
              <Row
                label={t("connection.permission")}
                info={<FieldHint text={t("connection.permissionHint")} topic="connection" />}
              >
                {t(`projects.permission.${c.permission}`)}
              </Row>
              <Row label={t("projects.token")}>
                <span className="font-mono">…{c.credentialHint}</span>
              </Row>
              <Row
                label={t("projects.catalogue")}
                info={<FieldHint text={t("connection.catalogueHint")} topic="catalogue" />}
              >
                {c.lastSyncAt ? dateTime(c.lastSyncAt, i18n.language) : t("projects.neverSynced")}
              </Row>
            </>
          )}
        </dl>
        <div className="mt-4 flex flex-wrap gap-2">
          {admin && (
            <Button
              variant="secondary"
              icon={<KeyRound className="size-4" aria-hidden />}
              onClick={() => setDialog("token")}
            >
              {t("projects.replaceToken")}
            </Button>
          )}
          <Button
            variant="secondary"
            busy={refresh.isPending}
            icon={<RefreshCw className="size-4" aria-hidden />}
            onClick={() =>
              refresh.mutate(projectId, {
                onSuccess: () =>
                  toast({
                    tone: "ok",
                    title: t("projects.refreshed", { name: project.data.name }),
                  }),
                onError: () => toast({ tone: "danger", title: t("common.unexpected") }),
              })
            }
          >
            {t("projects.refresh")}
          </Button>
        </div>
      </Card>

      {admin && (
        <Section title={t("connection.dangerTitle")} description={t("connection.dangerLead")}>
          <Card className="flex flex-wrap items-center justify-between gap-3 border-danger/40">
            <div className="flex max-w-xl flex-col gap-1">
              <p className="text-sm font-medium">{t("projects.forget")}</p>
              <p className="text-sm text-muted">{t("connection.forgetLead")}</p>
            </div>
            <Button
              variant="danger"
              icon={<Trash2 className="size-4" aria-hidden />}
              onClick={() => setDialog("forget")}
            >
              {t("projects.forget")}
            </Button>
          </Card>
        </Section>
      )}

      <ReplaceTokenDialog
        projectId={projectId}
        open={dialog === "token"}
        onOpenChange={closeDialog}
      />
      <RenameDialog
        projectId={projectId}
        name={project.data.name}
        open={dialog === "rename"}
        onOpenChange={closeDialog}
      />
      <ForgetProjectDialog
        project={project.data}
        open={dialog === "forget"}
        onOpenChange={closeDialog}
      />
    </>
  );
}
